;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { z } from "zod/v4";
import { ProviderType } from "@/types/dataProvider";

const blankStringToUndefined = (value: unknown) =>
	typeof value === "string" && value.trim().length === 0 ? undefined : value;

const blankOptionalString = z.preprocess(
	blankStringToUndefined,
	z.string().trim().min(1).optional(),
);

export const deleteDataProviderSchema = z.object({
	providerId: z.string().min(1, { error: "Provider ID is required" }),
	workspaceId: z.cuid(),
});

const editDataProviderDataSchema = z.discriminatedUnion("type", [
	// YARDI: allow credentials or legacy apiKey
	z.object({
		credentials: z.object({
			apiKey: z.string().min(1, { error: "API key is required" }),
		}),
		type: z.literal(ProviderType.YARDI),
	}),
	// APP_FOOLIO: allow credentials or legacy clientId/clientSecret
	z.object({
		credentials: z
			.object({
				clientId: z.string().optional(),
				clientSecret: z.string().optional(),
				databaseName: blankOptionalString,
				propertyGroupIds: z.array(z.string()).optional(),
			})
			.optional(),
		type: z.literal(ProviderType.APP_FOOLIO),
	}),
	// APPFOLIO_DATABASE: allow credentials or legacy clientId/clientSecret
	z.object({
		credentials: z
			.object({
				clientId: z.string().optional(),
				clientSecret: z.string().optional(),
				developerId: blankOptionalString,
			})
			.optional(),
		type: z.literal(ProviderType.APPFOLIO_DATABASE),
	}),
	// OPINIION: only allow credentials object
	z.object({
		credentials: z.object({
			email: z.email({ error: "Valid email is required" }).toLowerCase().trim(),
			password: z.string().min(1, { error: "Password is required" }),
		}),
		type: z.literal(ProviderType.OPINIION),
	}),
	// GADMIN: only allow credentials object
	z.object({
		credentials: z.object({
			superAdminEmail: z
				.email({
					error: "Valid super admin email is required",
				})
				.toLowerCase()
				.trim(),
		}),
		type: z.literal(ProviderType.GADMIN),
	}),
	// GMELIUS: only allow credentials object
	z.object({
		credentials: z.object({
			clientId: z.string().min(1, { error: "Client ID is required" }),
			clientSecret: z.string().min(1, { error: "Client Secret is required" }),
		}),
		type: z.literal(ProviderType.GMELIUS),
	}),

	// QUICKBOOKS
	z.object({
		credentials: z.object({
			clientId: blankOptionalString,
			clientSecret: blankOptionalString,
		}),
		type: z.literal(ProviderType.QUICKBOOKS),
	}),
	// ZOOM
	z.object({
		credentials: z
			.object({
				clientId: z.string().optional(),
				clientSecret: z.string().optional(),
			})
			.optional(),
		type: z.literal(ProviderType.ZOOM),
	}),
	// GOTO_CONNECT: OAuth-only provider shell
	z.object({
		credentials: z.object({}).optional(),
		type: z.literal(ProviderType.GOTO_CONNECT),
	}),
	// PODIUM
	z.object({
		credentials: z
			.object({
				clientId: z.string().optional(),
				clientSecret: z.string().optional(),
			})
			.optional(),
		type: z.literal(ProviderType.PODIUM),
	}),

	// CALL_LINKS
	z.object({
		credentials: z.object({
			databaseName: blankOptionalString,
		}),
		type: z.literal(ProviderType.CALL_LINKS),
	}),
	// SHOWMOJO
	z.object({
		credentials: z.object({
			apiToken: z.string().trim().min(1, { error: "API token is required" }),
		}),
		type: z.literal(ProviderType.SHOWMOJO),
	}),
	// ASANA
	z.object({
		credentials: z
			.object({
				personalAccessToken: blankOptionalString,
				workspaceGid: blankOptionalString,
				workspaceName: blankOptionalString,
			})
			.optional(),
		projectIds: z.array(z.string()).optional(),
		projectNames: z.array(z.string()).optional(),
		type: z.literal(ProviderType.ASANA),
	}),
	// RENTVINE
	z.object({
		credentials: z.object({
			clientId: z.preprocess(
				blankStringToUndefined,
				z.string().trim().min(1, { error: "Client ID is required" }).optional(),
			),
			clientSecret: z.preprocess(
				blankStringToUndefined,
				z.string().min(1, { error: "Client Secret is required" }).optional(),
			),
			databaseName: z
				.string()
				.trim()
				.min(1, { error: "Database name is required" }),
			generalWorkOrderVendorContactIds: z
				.array(z.string().trim().min(1))
				.optional(),
		}),
		type: z.literal(ProviderType.RENTVINE),
	}),
	// RENTENGINE
	z.object({
		credentials: z.object({
			accountId: blankOptionalString,
			apiKey: z.string().trim().min(1, { error: "API key is required" }),
		}),
		type: z.literal(ProviderType.RENTENGINE),
	}),
]);

export const editDataProviderSchema = z
	.object({
		data: z.looseObject({
			credentials: z.looseObject({}).optional(),
			projectIds: z.array(z.string()).optional(),
			projectNames: z.array(z.string()).optional(),
			propertyGroupIds: z.array(z.string()).optional(),
		}),
		provider: z.object({
			id: z.string().min(1, { error: "Provider ID is required" }),
			type: z.enum(Object.values(ProviderType) as [string, ...string[]]),
		}),
		workspaceId: z.cuid(),
	})
	.transform((value, context) => {
		const parsedData = editDataProviderDataSchema.safeParse({
			...value.data,
			type: value.provider.type,
		});
		if (!parsedData.success) {
			for (const issue of parsedData.error.issues) {
				context.addIssue({
					...issue,
					path: ["data", ...issue.path],
				});
			}
			return z.NEVER;
		}

		return {
			data: parsedData.data,
			provider: value.provider,
			workspaceId: value.workspaceId,
		};
	});

export const createDataProviderSchema = z.object({
	data: z.discriminatedUnion("type", [
		// YARDI: allow credentials or legacy apiKey
		z.object({
			credentials: z.object({
				apiKey: z.string().min(1, { error: "API key is required" }),
			}),
			type: z.literal(ProviderType.YARDI),
		}),
		// APP_FOOLIO: allow credentials or legacy clientId/clientSecret
		z.object({
			credentials: z.object({
				clientId: z.string().min(25, { error: "Client ID is required" }),
				clientSecret: z
					.string()
					.min(32, { error: "Client Secret is required" }),
				databaseName: z.string().min(1, { error: "Database name is required" }),
			}),
			// Frontend step 2: selected property groups (optional)
			propertyGroupIds: z
				.array(z.union([z.number(), z.string()]))
				.min(1, { error: "Please select at least one property group" }),
			type: z.literal(ProviderType.APP_FOOLIO),
		}),
		// APPFOLIO_DATABASE: allow credentials or legacy clientId/clientSecret
		z.object({
			credentials: z.object({
				clientId: z.string().min(25, { error: "Client ID is required" }),
				clientSecret: z
					.string()
					.min(32, { error: "Client Secret is required" }),
				developerId: z.string().min(1, { error: "Developer ID is required" }),
			}),
			type: z.literal(ProviderType.APPFOLIO_DATABASE),
		}),
		// OPINIION: only allow credentials object
		z.object({
			credentials: z.object({
				email: z
					.email({ error: "Valid email is required" })
					.toLowerCase()
					.trim(),
				password: z.string().min(1, { error: "Password is required" }),
			}),
			type: z.literal(ProviderType.OPINIION),
		}),
		// GADMIN: only allow credentials object
		z.object({
			credentials: z.object({
				superAdminEmail: z
					.email({ error: "Valid email is required" })
					.toLowerCase()
					.trim(),
			}),
			type: z.literal(ProviderType.GADMIN),
		}),
		// GMELIUS: only allow credentials object
		z.object({
			credentials: z.object({
				clientId: z.string().min(1, { error: "Client ID is required" }),
				clientSecret: z.string().min(1, { error: "Client Secret is required" }),
			}),
			type: z.literal(ProviderType.GMELIUS),
		}),
		// QUICKBOOKS
		z.object({
			credentials: z.object({
				clientId: z.string().min(0).optional(),
				clientSecret: z.string().min(0).optional(),
			}),
			type: z.literal(ProviderType.QUICKBOOKS),
		}),
		// ZOOM
		z.object({
			credentials: z
				.object({
					clientId: z.string().optional(),
					clientSecret: z.string().optional(),
				})
				.optional(),
			type: z.literal(ProviderType.ZOOM),
		}),
		// GOTO_CONNECT: OAuth-only provider shell
		z.object({
			credentials: z.object({}).optional(),
			type: z.literal(ProviderType.GOTO_CONNECT),
		}),
		// PODIUM
		z.object({
			credentials: z
				.object({
					clientId: z.string().optional(),
					clientSecret: z.string().optional(),
				})
				.optional(),
			type: z.literal(ProviderType.PODIUM),
		}),
		// CALL_LINKS
		z.object({
			credentials: z.object({
				databaseName: z.string().min(1, { error: "Database name is required" }),
			}),
			type: z.literal(ProviderType.CALL_LINKS),
		}),
		// SHOWMOJO
		z.object({
			credentials: z.object({
				apiToken: z.string().trim().min(1, { error: "API token is required" }),
			}),
			type: z.literal(ProviderType.SHOWMOJO),
		}),
		// ASANA
		z.object({
			credentials: z.object({
				personalAccessToken: z
					.string()
					.trim()
					.min(1, { error: "Personal access token is required" }),
				workspaceGid: z
					.string()
					.trim()
					.min(1, { error: "Workspace is required" }),
				workspaceName: z.string().trim().min(1).optional(),
			}),
			projectIds: z
				.array(z.string().trim().min(1))
				.min(1, { error: "Please select at least one project" }),
			projectNames: z.array(z.string().trim().min(1)).optional(),
			type: z.literal(ProviderType.ASANA),
		}),
		// RENTVINE
		z.object({
			credentials: z.object({
				clientId: z.string().trim().min(1, { error: "Client ID is required" }),
				clientSecret: z.string().min(1, { error: "Client Secret is required" }),
				databaseName: z
					.string()
					.trim()
					.min(1, { error: "Database name is required" }),
				generalWorkOrderVendorContactIds: z
					.array(z.string().trim().min(1))
					.optional(),
			}),
			type: z.literal(ProviderType.RENTVINE),
		}),
		// RENTENGINE
		z.object({
			credentials: z.object({
				accountId: z.string().trim().min(1).optional(),
				apiKey: z.string().trim().min(1, { error: "API key is required" }),
			}),
			type: z.literal(ProviderType.RENTENGINE),
		}),
	]),
	workspaceId: z.cuid(),
});

const asanaPingDataProviderSchema = z.object({
	credentials: z.object({
		personalAccessToken: z.string().trim().optional(),
		workspaceGid: z.string().trim().min(1).optional(),
		workspaceName: z.string().trim().min(1).optional(),
	}),
	projectIds: z.array(z.string()).optional(),
	projectNames: z.array(z.string()).optional(),
	type: z.literal(ProviderType.ASANA),
});

const rentvinePingDataProviderSchema = z.object({
	credentials: z.object({
		clientId: z.string().trim().optional(),
		clientSecret: z.string().optional(),
		databaseName: z
			.string()
			.trim()
			.min(1, { error: "Database name is required" }),
		generalWorkOrderVendorContactIds: z
			.array(z.string().trim().min(1))
			.optional(),
	}),
	type: z.literal(ProviderType.RENTVINE),
});

export const pingDataProviderSchema = z.object({
	data: z.union([
		createDataProviderSchema.shape.data,
		asanaPingDataProviderSchema,
		rentvinePingDataProviderSchema,
	]),
	workspaceId: z.cuid(),
});

export type DeleteDataProviderSchema = z.infer<typeof deleteDataProviderSchema>;
export type EditDataProviderFormValues = z.input<typeof editDataProviderSchema>;
export type EditDataProviderSchema = z.infer<typeof editDataProviderSchema>;
export type CreateDataProviderSchema = z.infer<typeof createDataProviderSchema>;
export type PingDataProviderSchema = z.infer<typeof pingDataProviderSchema>;
